/**
 * Scenario compare (FA pack — FA7; A9/E6 hardening 2026-08-19).
 *
 * Side-by-side compare card for Base / Upside / Downside scenarios.
 * Renders a grid with key recomputed metrics per scenario plus a
 * "Set canonical" star that the LLM block reads from. Edits call
 * `onUpdate(scenarios)` with the new scenarios array — the host commits
 * via the existing store action.
 *
 * What changed in the A9/E6 pass:
 *  - Scenario EDITOR. Each scenario's CAPEX / capacity-factor / WACC / PPA
 *    adjustments are editable in place, as RELATIVE swings (×, ±) or an
 *    absolute pin (=). The draft is previewed locally — LCOE/IRR/NPV/payback
 *    recompute as you type — and only committed on Apply, so a keystroke is
 *    never a store write. The draft holds the RAW typed string (never a
 *    re-formatted number), so the decimal separator survives (cf. A3).
 *  - "Clear diff" CONFIRMS first, and Remove is available for the LAST
 *    scenario too — removing it returns the Seed CTA, which used to be
 *    unreachable forever after the first seed.
 *  - Honest "Set canonical" copy: it moves THIS panel's baseline and the
 *    basis the report/LLM read at the NEXT generation. It does not move the
 *    persisted portfolio composite, which is scored from each report's
 *    stamped `financialAssumptionsAtGen` until those reports regenerate.
 *  - Baseline is the EXPLICIT canonical, or the unmodified base — never an
 *    arbitrary `scenarios[0]`. A missing / duplicated canonical flag is
 *    surfaced as a diagnostic instead of being silently resolved.
 *
 * Pure read on `FinancialAssumptions`. The compare card's CTA is
 * "Seed defaults from base" when `fa.scenarios` is empty or missing.
 */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Star, RefreshCw, Plus, Trash2, SlidersHorizontal } from 'lucide-react';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import {
  baseMergedScenario,
  mergeAllScenarios,
  mergeScenarioWithMetrics,
  resolveCanonicalCore,
  scenarioAdjustments,
  seedDefaultScenariosFor,
  setCanonicalScenario,
  summariseScenario,
  type FinancialScenarioWithAdjustments,
  type MergedScenario,
  type ScenarioAdjustableField,
  type ScenarioAdjustmentKind,
  type ScenarioAdjustments,
} from '../../../lib/financialScenarios';
import { formatMetric } from '../../../lib/financialSensitivity';
import { stableStringify } from '../../../lib/ops/jobIdempotencyShared';
import {
  ScenarioProjectionError,
  projectCompositeUnderScenario,
  type ProjectedComposite,
} from '../../../lib/portfolio/scenarioCompositeProjection';
import { PILLARS, type PillarId } from '../../../lib/portfolio/pillarRegistry';
import type {
  FinancialAssumptions,
  FinancialScenario,
} from '../../../types/financialAssumptions';
import type { SiteEntity } from '../../../types/site';
import type { SiteRagReport } from '../../../types/siteRag';

export interface ScenarioComparePanelProps {
  /** Current FA — including the canonical core + scenario diffs. */
  fa: FinancialAssumptions | null;
  site: SiteEntity;
  reports: SiteRagReport[];
  /** Called with the new `scenarios` array. Host commits to the store. */
  onUpdate: (scenarios: FinancialScenario[]) => Promise<void> | void;
  onRecomputeComposite?: () => Promise<void> | void;
}

type ScenarioProjectionResult = {
  projected: ProjectedComposite | null;
  error: string | null;
  cacheKey: string;
};

/**
 * The drivers the in-panel editor owns. Anything else on a scenario
 * (hand-authored absolute overrides on other fields) is left untouched by
 * the editor and still merges normally.
 */
const EDITABLE_DRIVERS: ReadonlyArray<{
  field: ScenarioAdjustableField;
  label: string;
  unit: string;
}> = [
  { field: 'capexTotalUsd', label: 'CAPEX', unit: 'USD' },
  { field: 'capacityFactorPct', label: 'Capacity factor', unit: '%' },
  { field: 'waccPct', label: 'WACC', unit: '%' },
  { field: 'ppaPricePerMwhUsd', label: 'PPA price', unit: '$/MWh' },
];

const KIND_OPTIONS: ReadonlyArray<{ value: ScenarioAdjustmentKind; label: string; help: string }> = [
  { value: 'multiplier', label: '×', help: 'multiply the base value (0.9 = -10%)' },
  { value: 'delta', label: '±', help: 'add to the base value (+5 = +5 points)' },
  { value: 'absolute', label: '=', help: 'pin the value regardless of the base' },
];

/** One editor row's in-flight state — `raw` is exactly what the user typed. */
type DraftRow = { kind: ScenarioAdjustmentKind; raw: string };
type DraftMap = Partial<Record<ScenarioAdjustableField, DraftRow>>;

type PendingAction = { kind: 'clear' | 'remove'; id: string; label: string; isLast: boolean };

function hashStable(input: string): string {
  let h = 0;
  for (let i = 0; i < input.length; i += 1) {
    h = ((h << 5) - h + input.charCodeAt(i)) | 0;
  }
  return Math.abs(h).toString(36);
}

/** Permissive parse for the editor buffer. Empty / unparseable ⇒ null (no adjustment). */
function parseAdjustmentValue(raw: string): number | null {
  const cleaned = raw.replace(/[,\s]/g, '');
  if (cleaned === '' || cleaned === '-' || cleaned === '.' || cleaned === '-.') return null;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Seed the editor from what the scenario actually says today: its relative
 * adjustment if it has one, otherwise its (legacy) absolute override shown as
 * an `=` pin, otherwise an empty multiplier row.
 */
function draftFromScenario(scenario: FinancialScenarioWithAdjustments): DraftMap {
  const adjustments = scenarioAdjustments(scenario);
  const assumptions = (scenario.assumptions ?? {}) as Record<string, unknown>;
  const draft: DraftMap = {};
  for (const driver of EDITABLE_DRIVERS) {
    const adjustment = adjustments[driver.field];
    if (adjustment) {
      draft[driver.field] = { kind: adjustment.kind, raw: String(adjustment.value) };
      continue;
    }
    const absolute = assumptions[driver.field];
    if (typeof absolute === 'number' && Number.isFinite(absolute)) {
      draft[driver.field] = { kind: 'absolute', raw: String(absolute) };
      continue;
    }
    draft[driver.field] = { kind: 'multiplier', raw: '' };
  }
  return draft;
}

/**
 * Fold the editor draft back into a scenario.
 *
 * The editor OWNS its four drivers: for those fields the draft is the whole
 * truth, so any stale absolute override on them is dropped (that is the
 * migration path off the frozen seeded numbers A9 describes). Overrides on
 * every other field are preserved verbatim.
 */
function applyDraft(
  scenario: FinancialScenarioWithAdjustments,
  draft: DraftMap,
): FinancialScenarioWithAdjustments {
  const nextAdjustments: ScenarioAdjustments = { ...scenarioAdjustments(scenario) };
  const nextAssumptions = { ...(scenario.assumptions ?? {}) };
  for (const driver of EDITABLE_DRIVERS) {
    delete nextAssumptions[driver.field];
    const row = draft[driver.field];
    const value = row ? parseAdjustmentValue(row.raw) : null;
    if (row && value != null) nextAdjustments[driver.field] = { kind: row.kind, value };
    else delete nextAdjustments[driver.field];
  }
  const next: FinancialScenarioWithAdjustments = { ...scenario, assumptions: nextAssumptions };
  if (Object.keys(nextAdjustments).length > 0) next.adjustments = nextAdjustments;
  else delete next.adjustments;
  return next;
}

/** Strip BOTH override channels — what "Clear diff" commits. */
function clearScenarioDiff(scenario: FinancialScenarioWithAdjustments): FinancialScenarioWithAdjustments {
  const next: FinancialScenarioWithAdjustments = { ...scenario, assumptions: {} };
  delete next.adjustments;
  return next;
}

/**
 * Decision-band crossing chip → Aurora subtle status tint. Returns an inline
 * style (background + border) drawn from the theme-flipping subtle tokens (see
 * :root / .light in index.css) so the chip gets a dark-appropriate tint in dark
 * mode and a light wash in light mode. Text uses --text-primary (black in light,
 * white in dark): the --*-foreground tokens are sized for the SATURATED --*
 * fill (white in light / near-black in dark) and read inverted on the subtle
 * tint — instead of the old fixed light pastel that never flipped.
 *   green  -> success | amber -> warning | orange -> conditional-high | red -> danger
 */
function bandChipStyle(color: string): React.CSSProperties {
  switch (color) {
    case 'green':
      return { backgroundColor: 'var(--success-subtle)', borderColor: 'var(--success)', color: 'var(--text-primary)' };
    case 'amber':
      return { backgroundColor: 'var(--warning-subtle)', borderColor: 'var(--warning)', color: 'var(--text-primary)' };
    case 'orange':
      return { backgroundColor: 'var(--conditional-high-subtle)', borderColor: 'var(--conditional-high)', color: 'var(--text-primary)' };
    case 'red':
      return { backgroundColor: 'var(--danger-subtle)', borderColor: 'var(--danger)', color: 'var(--text-primary)' };
    default:
      return { backgroundColor: 'var(--surface-1)', borderColor: 'var(--border)', color: 'var(--text-secondary)' };
  }
}

// Delta tone: a NEGATIVE delta (cheaper / better) reads success, a POSITIVE
// delta (worse) reads danger. Uses theme tokens so the tone flips dark/light.
function deltaClass(delta: number): string {
  if (delta < 0) return 'text-success';
  if (delta > 0) return 'text-danger';
  return 'text-text-secondary';
}

function formatSignedDelta(delta: number): string {
  if (Math.abs(delta) < 0.005) return '→ 0.00';
  return `${delta > 0 ? '↑' : '↓'} ${Math.abs(delta).toFixed(2)}`;
}

function projectionErrorMessage(err: unknown): string {
  if (err instanceof ScenarioProjectionError) return err.message;
  return 'Projection unavailable — incomplete scenario inputs';
}

export function ScenarioComparePanel({
  fa,
  site,
  reports,
  onUpdate,
  onRecomputeComposite,
}: ScenarioComparePanelProps): React.JSX.Element {
  const merged: MergedScenario[] = useMemo(
    () => (fa ? mergeAllScenarios(fa) : []),
    [fa],
  );
  const [confirmCanonicalId, setConfirmCanonicalId] = useState<string | null>(null);
  const [canonicalUpdating, setCanonicalUpdating] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<DraftMap>({});
  const projectionCacheRef = useRef<Map<string, ScenarioProjectionResult>>(new Map());

  const financialAssumptionsHash = useMemo(() => {
    if (!fa) return '';
    const projectionRelevant = {
      ...fa,
      updatedAt: undefined,
      fieldProvenance: undefined,
    };
    return hashStable(stableStringify(projectionRelevant));
  }, [fa]);

  /**
   * Data-integrity diagnostic on the canonical flag — rendered, not swallowed.
   * `resolveCanonicalCore` is pure and returns it rather than warning.
   */
  const canonicalDiagnostic = useMemo(
    () => (fa ? resolveCanonicalCore(fa).diagnostic : null),
    [fa],
  );

  /**
   * Projections for every scenario, plus the BASELINE the deltas are measured
   * against: the explicitly-canonical scenario, or the unmodified base when
   * none is flagged.
   */
  const projections = useMemo(() => {
    const byId: Record<string, ScenarioProjectionResult> = {};
    const project = (row: MergedScenario): ScenarioProjectionResult => {
      const cacheKey = `${site.id}::${row.scenario.id}::${financialAssumptionsHash}`;
      const cached = projectionCacheRef.current.get(cacheKey);
      if (cached) return cached;
      let result: ScenarioProjectionResult;
      try {
        result = {
          projected: projectCompositeUnderScenario(site, reports, row),
          error: null,
          cacheKey,
        };
      } catch (err) {
        result = { projected: null, error: projectionErrorMessage(err), cacheKey };
      }
      projectionCacheRef.current.set(cacheKey, result);
      return result;
    };
    for (const row of merged) byId[row.scenario.id] = project(row);
    const canonicalRow = merged.find((row) => row.scenario.isCanonical) ?? null;
    const baseline = canonicalRow
      ? byId[canonicalRow.scenario.id] ?? null
      : (fa && merged.length > 0 ? project(baseMergedScenario(fa)) : null);
    return { byId, baseline };
  }, [fa, financialAssumptionsHash, merged, reports, site]);

  const projectionByScenarioId = projections.byId;
  const canonicalProjection = projections.baseline;

  /** Local preview of the scenario being edited — recomputes as you type. */
  const previewRow = useMemo(() => {
    if (!fa || !editingId) return null;
    const source = (fa.scenarios ?? []).find((s) => s.id === editingId) as
      FinancialScenarioWithAdjustments | undefined;
    if (!source) return null;
    return mergeScenarioWithMetrics(fa, applyDraft(source, draft));
  }, [draft, editingId, fa]);

  const onSeedDefaults = useCallback(() => {
    if (!fa) return;
    void onUpdate(seedDefaultScenariosFor(fa));
  }, [fa, onUpdate]);

  const onSetCanonical = useCallback((id: string) => {
    const selected = merged.find((m) => m.scenario.id === id)?.scenario;
    if (!selected || selected.isCanonical) return;
    setConfirmCanonicalId(id);
  }, [merged]);

  const onConfirmCanonical = useCallback(async () => {
    if (!fa || !confirmCanonicalId) return;
    setCanonicalUpdating(true);
    try {
      await onUpdate(setCanonicalScenario(fa.scenarios ?? [], confirmCanonicalId));
      await onRecomputeComposite?.();
    } finally {
      setCanonicalUpdating(false);
      setConfirmCanonicalId(null);
    }
  }, [confirmCanonicalId, fa, onRecomputeComposite, onUpdate]);

  const onCancelCanonical = useCallback(() => {
    if (canonicalUpdating) return;
    setConfirmCanonicalId(null);
  }, [canonicalUpdating]);

  const onStartEdit = useCallback((scenario: FinancialScenarioWithAdjustments) => {
    setEditingId(scenario.id);
    setDraft(draftFromScenario(scenario));
  }, []);

  const onDiscardEdit = useCallback(() => {
    setEditingId(null);
    setDraft({});
  }, []);

  const onDraftKind = useCallback((field: ScenarioAdjustableField, kind: ScenarioAdjustmentKind) => {
    setDraft((prev) => ({ ...prev, [field]: { kind, raw: prev[field]?.raw ?? '' } }));
  }, []);

  const onDraftValue = useCallback((field: ScenarioAdjustableField, raw: string) => {
    setDraft((prev) => ({ ...prev, [field]: { kind: prev[field]?.kind ?? 'multiplier', raw } }));
  }, []);

  const onApplyEdit = useCallback(() => {
    if (!fa || !editingId) return;
    const next = (fa.scenarios ?? []).map((s) => (
      s.id === editingId ? applyDraft(s as FinancialScenarioWithAdjustments, draft) : s
    ));
    void onUpdate(next);
    setEditingId(null);
    setDraft({});
  }, [draft, editingId, fa, onUpdate]);

  const onRequestClear = useCallback((scenario: FinancialScenario, isLast: boolean) => {
    setPendingAction({ kind: 'clear', id: scenario.id, label: scenario.label, isLast });
  }, []);

  const onRequestRemove = useCallback((scenario: FinancialScenario, isLast: boolean) => {
    setPendingAction({ kind: 'remove', id: scenario.id, label: scenario.label, isLast });
  }, []);

  const onConfirmPendingAction = useCallback(() => {
    if (!fa || !pendingAction) return;
    const scenarios = fa.scenarios ?? [];
    if (pendingAction.kind === 'clear') {
      const next = scenarios.map((s) => (
        s.id === pendingAction.id ? clearScenarioDiff(s as FinancialScenarioWithAdjustments) : s
      ));
      void onUpdate(next);
    } else {
      const next = scenarios.filter((s) => s.id !== pendingAction.id);
      // If the removed scenario was canonical, promote the first remaining one
      // so the record keeps exactly one explicit canonical.
      if (next.length > 0 && !next.some((s) => s.isCanonical)) {
        next[0] = { ...next[0], isCanonical: true };
      }
      void onUpdate(next);
    }
    if (editingId === pendingAction.id) onDiscardEdit();
    setPendingAction(null);
  }, [editingId, fa, onDiscardEdit, onUpdate, pendingAction]);

  const onAddScenario = useCallback(() => {
    if (!fa) return;
    const existing = fa.scenarios ?? [];
    const newId = `scenario-${existing.length + 1}-${Date.now().toString(36)}`;
    const next: FinancialScenario[] = [
      ...existing,
      { id: newId, label: `Scenario ${existing.length + 1}`, assumptions: {}, isCanonical: existing.length === 0 },
    ];
    void onUpdate(next);
  }, [fa, onUpdate]);

  const confirmTargetScenario = useMemo(
    () => merged.find((m) => m.scenario.id === confirmCanonicalId) ?? null,
    [confirmCanonicalId, merged],
  );
  const confirmTargetProjection = confirmTargetScenario
    ? projectionByScenarioId[confirmTargetScenario.scenario.id] ?? null
    : null;

  if (!fa) {
    return (
      <div className="border border-border bg-surface-1 rounded-lg p-4 text-[11px] text-text-muted">
        Save financial assumptions above to compare Base / Upside / Downside scenarios.
      </div>
    );
  }

  if (merged.length === 0) {
    return (
      <div className="border border-border bg-surface-1 rounded-lg">
        <div className="px-4 py-3 border-b border-border">
          <div className="text-[11px] font-bold uppercase tracking-widest text-text-primary">
            Scenarios
          </div>
          <div className="text-[10px] text-text-muted mt-0.5">
            Compare Base / Upside / Downside side-by-side. The canonical scenario
            (★) is what the LLM and reports read from.
          </div>
        </div>
        <div className="px-4 py-6 text-center space-y-3">
          <p className="text-[11px] text-text-secondary">
            No scenarios defined. Seed three defaults with conventional
            ±swings on CAPEX, capacity factor, and WACC.
          </p>
          <Button size="sm" onClick={onSeedDefaults} data-testid="scenario-seed-cta">
            Seed Base / Upside / Downside
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-border bg-surface-1 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-widest text-text-primary">
            Scenarios
          </div>
          <div className="text-[10px] text-text-muted mt-0.5">
            {merged.length} scenario{merged.length === 1 ? '' : 's'} · ★ canonical is the basis reports
            and the LLM read at the next generation
          </div>
          {canonicalDiagnostic && (
            <div
              className="text-[10px] font-medium text-warning mt-1"
              data-testid="scenario-canonical-diagnostic"
            >
              ⚠ {canonicalDiagnostic.message}
            </div>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={onAddScenario}>
          <Plus className="w-3 h-3 mr-1" /> Add
        </Button>
      </div>

      <div className="grid" style={{ gridTemplateColumns: `repeat(${merged.length}, minmax(0, 1fr))` }}>
        {merged.map((row) => {
          const scenario = row.scenario;
          const isEditing = editingId === scenario.id;
          const view = isEditing && previewRow ? previewRow : row;
          const metrics = view.metrics;
          const summary = summariseScenario(view.scenario);
          const projectionResult = projectionByScenarioId[scenario.id];
          const projected = projectionResult?.projected;
          const isCanonical = scenario.isCanonical;
          const isNonCanonicalBandCross = !isCanonical && !!projected?.bandCrossing && !!canonicalProjection?.projected;
          const shiftedPillars = projected
            ? (Object.entries(projected.pillarShifts) as Array<[PillarId, { from: number; to: number; delta: number }]>)
              .filter(([, shift]) => Math.abs(shift.delta) >= 0.01)
              .sort((a, b) => Math.abs(b[1].delta) - Math.abs(a[1].delta))
              .slice(0, 5)
            : [];

          return (
            <div
              key={scenario.id}
              className={`border-r border-border last:border-r-0 p-3 space-y-2 ${
                isCanonical ? 'bg-primary/5' : ''
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <button
                    type="button"
                    onClick={() => onSetCanonical(scenario.id)}
                    className="inline-flex items-center gap-1 text-[11px] font-bold text-text-primary hover:text-primary"
                    aria-label={isCanonical ? 'Canonical scenario' : 'Set as canonical'}
                  >
                    <Star
                      className="w-3 h-3"
                      style={
                        isCanonical
                          ? { fill: 'var(--warning)', color: 'var(--warning)' }
                          : { color: 'var(--text-muted)' }
                      }
                    />
                    {scenario.label}
                  </button>
                  {isCanonical && (
                    <div className="text-[10px] font-semibold" style={{ color: 'var(--warning)' }}>
                      Canonical
                    </div>
                  )}
                  <div
                    className="type-body text-text-muted mt-0.5 truncate"
                    title={summary}
                    data-testid={`scenario-summary-${scenario.id}`}
                  >
                    {summary}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => (isEditing ? onDiscardEdit() : onStartEdit(scenario))}
                    title="Edit adjustments"
                    aria-label={`Edit ${scenario.label} adjustments`}
                    aria-expanded={isEditing}
                    className={`p-0.5 ${isEditing ? 'text-primary' : 'text-text-muted hover:text-text-secondary'}`}
                  >
                    <SlidersHorizontal className="w-3 h-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => onRequestClear(scenario, merged.length === 1)}
                    title="Clear diff"
                    aria-label={`Clear ${scenario.label} diff`}
                    className="text-text-muted hover:text-text-secondary p-0.5"
                  >
                    <RefreshCw className="w-3 h-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => onRequestRemove(scenario, merged.length === 1)}
                    title="Remove scenario"
                    aria-label={`Remove ${scenario.label}`}
                    className="text-text-muted hover:text-danger p-0.5"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>

              {isEditing && (
                <div
                  className="border border-border rounded-md p-2 space-y-2 bg-background/70"
                  data-testid={`scenario-editor-${scenario.id}`}
                >
                  <div className="type-section text-text-secondary">
                    Adjustments
                  </div>
                  {EDITABLE_DRIVERS.map((driver) => {
                    const rowDraft = draft[driver.field] ?? { kind: 'multiplier' as ScenarioAdjustmentKind, raw: '' };
                    return (
                      <div key={driver.field} className="space-y-0.5">
                        <div className="flex items-center justify-between text-[10px] text-text-secondary">
                          <span>{driver.label}</span>
                          <span className="text-text-muted">{driver.unit}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <select
                            aria-label={`${driver.label} adjustment kind`}
                            data-testid={`scenario-adjust-kind-${scenario.id}-${driver.field}`}
                            value={rowDraft.kind}
                            onChange={(e) => onDraftKind(driver.field, e.target.value as ScenarioAdjustmentKind)}
                            className="h-7 px-1 text-[11px] border border-border bg-surface-1 text-text-primary rounded-md"
                          >
                            {KIND_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value} title={opt.help}>{opt.label}</option>
                            ))}
                          </select>
                          <input
                            type="text"
                            inputMode="decimal"
                            aria-label={`${driver.label} adjustment value`}
                            data-testid={`scenario-adjust-value-${scenario.id}-${driver.field}`}
                            value={rowDraft.raw}
                            placeholder={rowDraft.kind === 'multiplier' ? '1.00' : ''}
                            onChange={(e) => onDraftValue(driver.field, e.target.value)}
                            className="h-7 min-w-0 flex-1 px-2 text-[11px] border border-border bg-surface-1 text-text-primary rounded-md tabular-nums"
                          />
                        </div>
                      </div>
                    );
                  })}
                  <div className="text-[9px] text-text-muted leading-snug">
                    Relative adjustments re-derive against the current base every time the model
                    changes. Metrics below preview the draft; the portfolio projection updates on Apply.
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="xs" onClick={onApplyEdit}>Apply</Button>
                    <Button size="xs" variant="outline" onClick={onDiscardEdit}>Discard</Button>
                  </div>
                </div>
              )}

              <div className="border border-border rounded-md p-2 space-y-1.5 bg-background/70">
                <div className="text-[10px] font-bold uppercase tracking-widest text-text-secondary">
                  Projected portfolio impact
                </div>
                {projectionResult?.error ? (
                  <div className="text-[11px] text-warning" data-testid={`scenario-projection-error-${scenario.id}`}>
                    Projection unavailable — incomplete scenario inputs
                  </div>
                ) : projected ? (
                  <>
                    <div className="text-[11px] font-semibold text-text-primary" data-testid={`scenario-projection-headline-${scenario.id}`}>
                      {projected.projectedBandLabel} · {projected.projectedComposite.toFixed(2)} · {projected.confidenceTag}
                    </div>
                    <div className={`text-[10px] font-medium ${deltaClass(projected.deltaFromCanonical)}`} data-testid={`scenario-projection-delta-${scenario.id}`}>
                      {isCanonical ? 'Canonical baseline' : `${formatSignedDelta(projected.deltaFromCanonical)} vs canonical`}
                    </div>
                    {isNonCanonicalBandCross && canonicalProjection?.projected && (
                      <div
                        className="text-[10px] font-semibold border rounded px-2 py-1"
                        style={bandChipStyle(projected.projectedBandColor)}
                        data-testid={`scenario-band-crossing-${scenario.id}`}
                      >
                        ⚠ Crosses {canonicalProjection.projected.projectedBandLabel} → {projected.projectedBandLabel} under {projected.scenarioLabel}
                      </div>
                    )}
                    {shiftedPillars.length > 0 && (
                      <div className="space-y-1 pt-1">
                        <div className="text-[10px] uppercase tracking-widest text-text-muted">Pillar shifts</div>
                        {shiftedPillars.map(([pillarId, shift]) => (
                          <div key={pillarId} className="flex items-center justify-between text-[10px] text-text-secondary">
                            <span>{PILLARS[pillarId].label}:</span>
                            <span className="tabular-nums">
                              {shift.from.toFixed(2)} → {shift.to.toFixed(2)} ({shift.delta >= 0 ? '+' : ''}{shift.delta.toFixed(2)})
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : null}
              </div>

              <MetricRow label="LCOE" value={formatMetric('lcoe', metrics.lcoeNominalPerMwhUsd)} testId={`scenario-metric-lcoe-${scenario.id}`} />
              <MetricRow label="IRR"  value={formatMetric('irr', metrics.projectIrrPct)} testId={`scenario-metric-irr-${scenario.id}`} />
              <MetricRow label="NPV"  value={formatMetric('npv', metrics.npvUsd)} testId={`scenario-metric-npv-${scenario.id}`} />
              <MetricRow label="Payback" value={metrics.paybackYears != null ? `${metrics.paybackYears.toFixed(1)} yr` : '—'} testId={`scenario-metric-payback-${scenario.id}`} />
            </div>
          );
        })}
      </div>

      <Dialog
        open={confirmCanonicalId != null}
        onOpenChange={(open) => {
          if (!open) onCancelCanonical();
        }}
      >
        <DialogContent size="md" showCloseButton={!canonicalUpdating}>
          <DialogHeader>
            <DialogTitle>Set canonical scenario</DialogTitle>
            <DialogDescription>
              {`Make "${confirmTargetScenario?.scenario.label ?? ''}" the canonical scenario?`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-[12px] text-text-secondary" data-testid="scenario-canonical-dialog-body">
            <div>
              <span className="font-semibold text-text-primary">Changes now:</span>{' '}
              this panel measures every other scenario against it, and the Financial Viability
              report and LLM prompt read it as the assumptions basis at the NEXT report generation.
            </div>
            <div>
              <span className="font-semibold text-text-primary">Does not change now:</span>{' '}
              the saved portfolio composite, dashboard band and site cards. Those are scored from
              the assumptions stamped on each existing report, so they only move once those
              reports are regenerated.
            </div>
            {confirmTargetProjection?.projected && canonicalProjection?.projected && (
              <div data-testid="scenario-canonical-dialog-projection">
                <span className="font-semibold text-text-primary">Projected once regenerated:</span>{' '}
                {confirmTargetProjection.projected.projectedBand === canonicalProjection.projected.projectedBand
                  ? `composite ${canonicalProjection.projected.projectedComposite.toFixed(2)} → ${confirmTargetProjection.projected.projectedComposite.toFixed(2)}, band unchanged (${confirmTargetProjection.projected.projectedBandLabel}).`
                  : `composite ${canonicalProjection.projected.projectedComposite.toFixed(2)} → ${confirmTargetProjection.projected.projectedComposite.toFixed(2)}, band ${canonicalProjection.projected.projectedBandLabel} → ${confirmTargetProjection.projected.projectedBandLabel}.`}
                {' '}This is a hypothetical projection, not a persisted score.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={onCancelCanonical} disabled={canonicalUpdating}>
              Cancel
            </Button>
            <Button onClick={() => { void onConfirmCanonical(); }} disabled={canonicalUpdating}>
              {canonicalUpdating ? 'Applying…' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={pendingAction != null}
        onOpenChange={(open) => {
          if (!open) setPendingAction(null);
        }}
      >
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle>
              {pendingAction?.kind === 'clear' ? 'Clear scenario overrides' : 'Remove scenario'}
            </DialogTitle>
            <DialogDescription>
              {pendingAction?.kind === 'clear'
                ? `"${pendingAction.label}" keeps its name but loses every override — both the absolute overrides and the relative adjustments. This cannot be undone.`
                : pendingAction?.isLast
                  ? `Removing "${pendingAction.label}" leaves no scenarios. Reports then read the unmodified base assumptions, and the Base / Upside / Downside seed becomes available again.`
                  : `"${pendingAction?.label ?? ''}" is removed from the compare set. If it was canonical, the first remaining scenario becomes canonical.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingAction(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={onConfirmPendingAction}>
              {pendingAction?.kind === 'clear' ? 'Clear diff' : 'Remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MetricRow({ label, value, testId }: { label: string; value: string; testId?: string }): React.JSX.Element {
  return (
    <div className="flex items-center justify-between text-[10px]">
      <span className="text-text-muted uppercase tracking-widest">{label}</span>
      <span className="text-text-primary tabular-nums" data-testid={testId}>{value}</span>
    </div>
  );
}
